/**
 * Maps stock exchanges to their respective timezones.
 * Used for converting market data timestamps to the correct local time.
 */

const EXCHANGE_TIMEZONE_MAP: Record<string, string> = {
  // US Exchanges (Eastern Time)
  NASDAQ: "America/New_York",
  NYSE: "America/New_York",
  AMEX: "America/New_York",
  OTC: "America/New_York",

  // UK Exchange (London Time)
  LSE: "Europe/London",

  // European Exchanges
  AMS: "Europe/Amsterdam",
  XETRA: "Europe/Berlin",
};

/** Default timezone when exchange is unknown (US Eastern) */
const DEFAULT_TIMEZONE = "America/New_York";

/**
 * Get the timezone for a given stock exchange.
 *
 * @param exchange - The exchange code (e.g., 'NASDAQ', 'LSE')
 * @returns The IANA timezone identifier for the exchange
 *
 * @example
 * getExchangeTimezone('NASDAQ') // 'America/New_York'
 * getExchangeTimezone('LSE')    // 'Europe/London'
 */
export const getExchangeTimezone = (
  exchange: string | undefined | null
): string => {
  if (!exchange) {
    return DEFAULT_TIMEZONE;
  }

  return EXCHANGE_TIMEZONE_MAP[exchange.toUpperCase()] ?? DEFAULT_TIMEZONE;
};

/**
 * Regular continuous-trading session for an exchange, expressed as
 * minutes-from-midnight in that exchange's own local time. Used to clip
 * intraday charts to the trading day.
 */
export interface ExchangeTradingHours {
  openMinutes: number;
  closeMinutes: number;
}

const toMinutes = (hour: number, minute: number): number => hour * 60 + minute;

/**
 * Regular-session hours per exchange, in the exchange's local time. Pair with
 * EXCHANGE_TIMEZONE_MAP — the timezone parses the FMP timestamp, these bounds
 * clip it to the session. Non-US exchanges trade a different window, so the
 * US 09:30–16:00 assumption drops genuine volume/price (e.g. the LSE 08:00
 * open) when applied blindly.
 */
const EXCHANGE_TRADING_HOURS_MAP: Record<string, ExchangeTradingHours> = {
  // US Exchanges: 09:30–16:00 ET
  NASDAQ: { openMinutes: toMinutes(9, 30), closeMinutes: toMinutes(16, 0) },
  NYSE: { openMinutes: toMinutes(9, 30), closeMinutes: toMinutes(16, 0) },
  AMEX: { openMinutes: toMinutes(9, 30), closeMinutes: toMinutes(16, 0) },
  OTC: { openMinutes: toMinutes(9, 30), closeMinutes: toMinutes(16, 0) },

  // London: 08:00–16:30
  LSE: { openMinutes: toMinutes(8, 0), closeMinutes: toMinutes(16, 30) },

  // Euronext Amsterdam / XETRA: 09:00–17:30 CET
  AMS: { openMinutes: toMinutes(9, 0), closeMinutes: toMinutes(17, 30) },
  XETRA: { openMinutes: toMinutes(9, 0), closeMinutes: toMinutes(17, 30) },
};

/** Default session when exchange is unknown — US Eastern regular hours. */
const DEFAULT_TRADING_HOURS: ExchangeTradingHours = {
  openMinutes: toMinutes(9, 30),
  closeMinutes: toMinutes(16, 0),
};

/**
 * Get the regular-session trading hours for a given exchange.
 *
 * @param exchange - The exchange code (e.g., 'NASDAQ', 'LSE')
 * @returns Session open/close as minutes-from-midnight in exchange-local time
 *
 * @example
 * getExchangeTradingHours('NASDAQ') // { openMinutes: 570, closeMinutes: 960 }
 * getExchangeTradingHours('LSE')    // { openMinutes: 480, closeMinutes: 990 }
 */
export const getExchangeTradingHours = (
  exchange: string | undefined | null
): ExchangeTradingHours => {
  if (!exchange) {
    return DEFAULT_TRADING_HOURS;
  }

  return (
    EXCHANGE_TRADING_HOURS_MAP[exchange.toUpperCase()] ?? DEFAULT_TRADING_HOURS
  );
};

export default getExchangeTimezone;
